<template>
  <module-template
    id="scrolltainer"
    title
    :canEdit="$store.state.fileEnvModifyPermission && item?.name !== 'default'"
    canList
    :canDelete="canDelete"
    :deleteConfirm="false"
    @deleteItem="showConfirm = true"
    @editItem="editFileEnv"
    @list="listEnvs"
  >
    <template #title>
      <v-row dense class="px-1" align="center">
        {{ $t("global.concepts.file-environment") }}
        <v-icon color="white" class="px-3" size="9">pic-arrow-right</v-icon>
        <div>{{ fileEnv.name }}</div>
      </v-row>
    </template>

    <basic-card-horizontal :headers="headers" :item="item"
      ><template #[`item.label`]="{ item }">
        <file-env-chip
          :color="item.color"
          :label="item.label"
        ></file-env-chip> </template
      ><template #[`item.numFiles`]="{ item }">
        <v-progress-circular :size="21" v-if="loadingNumbers" indeterminate />
        <span v-else>{{ item.numFiles }}</span>
      </template>

      <template #[`item.numRuns`]="{ item }">
        <v-progress-circular :size="21" v-if="loadingNumbers" indeterminate />
        <span v-else>{{ item.numRuns }}</span>
      </template>

      <template #[`item.numBps`]="{ item }">
        <v-progress-circular :size="21" v-if="loadingNumbers" indeterminate />
        <span v-else>{{ item.numBps }}</span>
      </template>
    </basic-card-horizontal>

    <v-dialog v-model="showConfirm" persistent :width="500">
      <v-overlay absolute :value="deleting">
        <v-chip color="blue" dark>{{ $t("global.msg.deleting") }}</v-chip>
      </v-overlay>

      <v-card>
        <v-card-title>
          <span class="headline">{{ $t("global.action.confirm") }}</span>
        </v-card-title>
        <v-card-text>
          <span>{{ $t("global.action.confirm-continue") }}</span>
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn
            depressed
            data-cy-id="confirm-delete-button"
            color="error"
            class="is-action"
            @click="deleteFileEnv()"
          >
            {{ $t("global.action.yes") }}
          </v-btn>
          <v-btn depressed class="button" @click="showConfirm = false">
            {{ $t("global.action.no") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </module-template>
</template>

<script>
import { get, remove } from "@/model/api";
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import FileEnvChip from "@/components/FileEnvChip";
import BasicCardHorizontal from "@/components/BasicCardHorizontal";

export default {
  name: "FileEnv",
  components: {
    ModuleTemplate,
    FileEnvChip,
    BasicCardHorizontal,
  },

  computed: {
    canDelete() {
      return (
        this.$store.state.fileEnvDeletePermission && !!this.fileEnv?.deletable
      );
    },

    item() {
      const parent = this.fileEnv.parent === "" ? "-" : this.fileEnv.parent;
      return {
        parent: parent,
        label: this.fileEnv.label,
        color: this.fileEnv.color,
        vault: this.fileEnv.vault,
        retentionPolicy: this.fileEnv.retentionPolicyName || "-",
        numFiles: this.numFiles,
        numRuns: this.numRuns,
        numBps: this.numBpInstances,
      };
    },
  },
  data() {
    return {
      fileEnv: {},
      numFiles: 0,
      numRuns: 0,
      numBpInstances: 0,
      loadingNumbers: false,

      headers: [
        { text: this.$t("global.concepts.parent"), value: "parent" },
        { text: this.$t("global.environment.label"), value: "label" },
        { text: this.$t("global.administration.vault"), value: "vault" },
        {
          text: this.$t("global.retention-policy.label"),
          value: "retentionPolicy",
        },
        { text: this.$t("global.environment.number-files"), value: "numFiles" },
        { text: this.$t("global.environment.number-runs"), value: "numRuns" },
        { text: this.$t("global.environment.number-bps"), value: "numBps" },
      ],

      showConfirm: false,
      deleting: false,
    };
  },
  methods: {
    listEnvs() {
      this.$router.push({ name: "File envs" });
    },

    async getFileEnv(id) {
      return await get("/file-env/" + id)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          this.$store.commit("showError", error);
        });
    },

    async getDependenciesNumbers(id) {
      return await get("/file-env/" + id + "/dependencies-numbers").catch(
        (error) => {
          this.$store.commit("showError", error);
        }
      );
    },

    async hasChildEnvs(id) {
      return await get("/file-env/" + id + "/has-child-env")
        .then((response) => {
          return response;
        })
        .catch((error) => {
          this.$store.commit("showError", error);
        });
    },

    async deleteFileEnv() {
      this.deleting = true;
      await remove("/file-env/" + this.fileEnv.id)
        .then(() => {
          this.$router.push({ name: "File envs" });
          this.deleting = false;
          this.showConfirm = false;
        })
        .catch((e) => {
          this.$store.commit(
            "showError",
            this.$t("global." + e.message, e.arguments)
          );
          this.deleting = false;
          this.showConfirm = false;
        });
    },

    editFileEnv() {
      this.$router.push({
        name: "Edit file env",
        params: { fileEnvId: this.fileEnv.id },
      });
    },
  },
  async created() {
    this.loadingNumbers = true;
    const fileEnvId = this.$route.params.fileEnvId;

    this.fileEnv = await this.getFileEnv(fileEnvId);

    const dependenciesNumbers = await this.getDependenciesNumbers(fileEnvId);
    this.loadingNumbers = false;

    this.numFiles = dependenciesNumbers?.numFiles;
    this.numRuns = dependenciesNumbers?.numRuns;
    this.numBpInstances = dependenciesNumbers?.numBpInstances;
  },
};
</script>
